import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

export type ToastPalette = AlertColor | 'primary';

export interface ToastOptions {
  message: string;
  severity?: AlertColor;
  /** When set, overrides severity for color. 'primary' uses theme primary (white on brand color). */
  palette?: ToastPalette;
  duration?: number;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  /** Brand-colored toast (white text on theme primary). Used for new-mail notifications. */
  showBrand: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');
  const [palette, setPalette] = useState<ToastPalette | undefined>(undefined);
  const [duration, setDuration] = useState(6000);

  const showToast = useCallback(({ message, severity = 'info', palette, duration = 6000 }: ToastOptions) => {
    setMessage(message);
    setSeverity(severity);
    setPalette(palette);
    setDuration(duration);
    setOpen(true);
  }, []);

  const showSuccess = useCallback((message: string) => {
    showToast({ message, severity: 'success' });
  }, [showToast]);

  const showError = useCallback((message: string) => {
    showToast({ message, severity: 'error', duration: 8000 });
  }, [showToast]);

  const showWarning = useCallback((message: string) => {
    showToast({ message, severity: 'warning' });
  }, [showToast]);

  const showInfo = useCallback((message: string) => {
    showToast({ message, severity: 'info' });
  }, [showToast]);

  const showBrand = useCallback((message: string) => {
    showToast({ message, severity: 'info', palette: 'primary' });
  }, [showToast]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const isBrand = palette === 'primary';

  // Map MUI severity → palette token for the colored accent (border + icon).
  // For brand toasts (used for new-mail notifications), force primary.
  const accent: 'primary' | 'success' | 'error' | 'warning' | 'info' = isBrand
    ? 'primary'
    : (severity as 'success' | 'error' | 'warning' | 'info');

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo, showBrand }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        // Sit BELOW the 64px AppHeader so it never overlaps. Anchor stays
        // top-right; we just push it down by header height + a small gap.
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          top: { xs: 72, sm: 80 },
          right: { xs: 12, sm: 16 },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          // Quiet style: white background, theme-coloured left border + icon.
          // Reads as professional admin / SaaS, not consumer-app neon.
          icon={false}
          variant="outlined"
          sx={(theme) => ({
            minWidth: 320,
            maxWidth: 440,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderColor: theme.palette[accent].main,
            borderLeft: `4px solid ${theme.palette[accent].main}`,
            borderRadius: 1.5,
            boxShadow: theme.shadows[3],
            whiteSpace: 'pre-line',
            alignItems: 'flex-start',
            // MUI's outlined Alert tints the message colour — override to body.
            '& .MuiAlert-message': {
              color: 'text.primary',
              fontWeight: 500,
              flex: 1,
            },
            '& .MuiAlert-action': {
              color: 'text.secondary',
            },
          })}
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
